import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { toast } from '@/components/ui/use-toast';
import { v4 } from 'uuid';
const phoneUtil = PhoneNumberUtil.getInstance();

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const combinedDateTime = (date: Date | undefined, time: Date | undefined | null) => {
  if (time && date) {
    const combined = new Date(date);
    combined.setHours(time.getHours());
    combined.setMinutes(time.getMinutes());
    combined.setSeconds(time.getSeconds());
    combined.setMilliseconds(time.getMilliseconds());

    return combined;
  } else {
    return new Date();
  }
};

export const displayFullName = (
  user:
    | {
        firstName?: string | undefined;
        lastName?: string | undefined;
      }
    | undefined
) => {
  if (!user) return '-';
  const { firstName, lastName } = user;
  if (!firstName && !lastName) return '-';
  if (!firstName) return lastName;
  if (!lastName) return firstName;
  return `${firstName} ${lastName}`;
};
export const convertISODateToMMDDYYYY = (isoDateString: string | null | undefined | Date): string => {
  if (!isoDateString) return '';
  const date = new Date(isoDateString);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
};

export const getTimestampDisplay = (isoDate: string | null | undefined) => {
  if (!isoDate) return { timeDisplayText: '', hasTooltip: false };
  const commentDate = new Date(isoDate);
  const now = new Date();
  const timeDifference = now.getTime() - commentDate.getTime();
  const minutesDifference = Math.floor(timeDifference / (1000 * 60));
  const hoursDifference = Math.floor(minutesDifference / 60);
  if (hoursDifference < 24) {
    if (minutesDifference < 1) return { timeDisplayText: 'just now', hasTooltip: true };
    if (minutesDifference < 60)
      return {
        timeDisplayText: `${minutesDifference} ${minutesDifference === 1 ? 'min' : 'mins'} ago`,
        hasTooltip: true,
      };
    return { timeDisplayText: `${hoursDifference} ${hoursDifference === 1 ? 'hour' : 'hours'} ago`, hasTooltip: true };
  }

  return { timeDisplayText: `${convertISODateToMMDDYYYY(isoDate)} ${convertISOToTime(isoDate)}`, hasTooltip: false };
};

export const convertISOToTime = (isoDateString: Date | string | null | undefined): string => {
  if (!isoDateString) return '';
  const date = isoDateString instanceof Date ? isoDateString : new Date(isoDateString);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes < 10 ? '0' : ''}${minutes} ${ampm}`;
};

/** Format a Date as HH:MM duration (hours and minutes, no AM/PM). */
export const convertDateToDurationTime = (
  dateValue: Date | string | null | undefined
): string => {
  if (!dateValue) return '';
  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

export const removeNullValues = (obj: object) => {
  return Object.fromEntries(Object.entries(obj).filter(([, value]) => value && value !== null));
};

export const joinByComma = (array: string[], joinBy: string): string => {
  if (array && Array.isArray(array)) return array.join(joinBy);
  return '';
};
export function cleanObject<T extends object>(obj: T): T {
  if (Array.isArray(obj)) {
    return obj
      .map(cleanObject)
      .filter((item) => !(item === null || (Array.isArray(item) && item.length === 0))) as unknown as T;
  } else if (typeof obj === 'object' && obj !== null) {
    return Object.fromEntries(
      Object.entries(obj as object)
        .map(([key, value]) => [key, cleanObject(value) as T[keyof T]])
        .filter(([, value]) => value !== null && !(Array.isArray(value) && value.length === 0))
    ) as T;
  }
  return obj;
}
export const genUniqueId = () => {
  return v4();
};

export const capitalizeFirstLetter = (value: string | undefined) => {
  if (!value) return '';
  return value?.charAt(0)?.toUpperCase() + value?.slice(1)?.toLowerCase();
};
export const badgeClasses = 'h-6 w-[115px] justify-center font-medium';
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};
export const formatPhoneNumber = (phone: string | undefined | null) => {
  if (!phone) return '';
  const number = phoneUtil.parseAndKeepRawInput(phone);
  return phoneUtil.formatInOriginalFormat(number);
};

export const copyToClipboard = async (text: string | undefined | null) => {
  if (!text) return;
  if (navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        description: 'Copied to clipboard',
        duration: 3000,
      });
    } catch {
      toast({
        description: 'Failed to copy',
        duration: 3000,
      });
    }
  } else {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
    } catch (err) {
      console.error('Fallback: Oops, unable to copy', err);
    }
    document.body.removeChild(textarea);
  }
};

export const downloadCSV = async (fileName: string, fileUrl: string, onComplete: () => void, onError: () => void) => {
  try {
    const response = await fetch(fileUrl);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    onComplete();
    toast({
      description: `'${fileName}.csv' has been downloaded.`,
      duration: 5000,
    });
  } catch (error) {
    onError();
    toast({
      description: `Download failed. Please try again.`,
      variant: 'destructive',
      duration: 5000,
    });
  }
};

export const getFileNameFromContentDisposition = (contentDisposition?: string | null): string | undefined => {
  if (!contentDisposition) return undefined;

  const encodedMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedMatch?.[1]) return decodeURIComponent(encodedMatch[1].replace(/"/g, ''));

  const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return fileNameMatch?.[1];
};

export const downloadFilefromBuffer = (response: string | Blob, fileName: string, fileType: 'CSV' | 'PDF' | 'ZIP') => {
  try {
    const mimeTypes = {
      CSV: 'text/csv;charset=utf-8;',
      PDF: 'application/pdf',
      ZIP: 'application/zip',
    };
    const blob = new Blob([response], { type: mimeTypes[fileType] });

    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${fileName}`);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      description: `'${fileName}' has been downloaded.`,
      duration: 5000,
    });
  } catch (error) {
    toast({
      description: `Download failed. Please try again.`,
      variant: 'destructive',
      duration: 5000,
    });
  }
};

export const stringToHexColor = (str: string | undefined): string => {
  if (!str) return '#E7E7E7';
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += ('00' + value.toString(16)).slice(-2);
  }
  return color;
};

export const capitalizeEveryWord = (str: string): string =>
  str
    .split(' ')
    .map((word) =>
      word
        ? word
            .split('-')
            .map((part) => (part ? capitalizeFirstLetter(part) : part))
            .join('-')
        : word
    )
    .join(' ');
